import { SaveFormResult } from "../../../components/Posts/PostForm/types";
import {
  FormSaveCallback,
  PostSavedCallback,
  PostSavedRequestType,
} from "./bulk-create-post.types";

export type SavedPostFormRecord = {
  result: SaveFormResult;
  createdIds: string[];
  requestType: PostSavedRequestType;
};

export class BulkCreatePostActiveContext {
  private formSavedMap = new Map<string, SavedPostFormRecord>();
  private formSaveCallbackMap = new Map<string, FormSaveCallback>();
  private postSavedCallbacks = new Set<PostSavedCallback>();
  private currentPageReloaded = false;
  notifyFormSaved(
    id: string,
    result: SaveFormResult,
    createdIds: string[] = [],
    requestType: PostSavedRequestType = "create"
  ) {
    const record = { result, createdIds, requestType };
    this.formSavedMap.set(id, record);
    this.postSavedCallbacks.forEach((callback) => {
      try {
        callback(record);
      } catch (error) {
        console.error("Post-saved callback failed:", error);
      }
    });
  }

  /** Returns an unregister function. Callbacks live for the lifetime of the
   * context, so anything that outlives one bulk-create session must detach
   * itself — see the `isOpened` guard in bulkCreatePostModalSaga. */
  registerPostSavedCallback(callback: PostSavedCallback): () => void {
    this.postSavedCallbacks.add(callback);
    return () => {
      this.postSavedCallbacks.delete(callback);
    };
  }

  registerFormSaveCallback(id: string, callback: FormSaveCallback) {
    this.formSaveCallbackMap.set(id, callback);
  }

  getFormSaveCallback(id: string): FormSaveCallback | undefined {
    return this.formSaveCallbackMap.get(id);
  }

  isAnyFormSaved(): boolean {
    return [...this.formSavedMap.values()].some(Boolean);
  }
  getAllSavedForms(): SaveFormResult[] {
    return this.getAllSavedFormRecords().map(({ result }) => result);
  }
  getAllSavedFormRecords(): SavedPostFormRecord[] {
    return [...this.formSavedMap.values()].filter((record) =>
      Boolean(record?.result)
    );
  }

  markCurrentPageReloaded() {
    this.currentPageReloaded = true;
  }

  hasReloadedCurrentPage(): boolean {
    return this.currentPageReloaded;
  }
}

class Manager {
  private activeContext: BulkCreatePostActiveContext | undefined;
  getActive() {
    if (!this.activeContext) {
      this.activeContext = new BulkCreatePostActiveContext();
    }
    return this.activeContext;
  }
  newActiveContext() {
    this.activeContext = new BulkCreatePostActiveContext();
    return this.activeContext;
  }
}

export const bulkCreatePostContextManager = new Manager();
